import React, { useEffect, useState } from "react";
import { Alert, Box, Button, CircularProgress, Stack } from "@mui/material";
import PrintRoundedIcon from "@mui/icons-material/PrintRounded";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import { useNavigate, useParams } from "react-router-dom";
import { adminApi } from "../api/client";
import QuotationPrintView, { QuotationPrintHeaderDraft } from "../components/QuotationPrintView";

function parseJsonField(value: unknown) {
  if (!value) {
    return {};
  }

  if (typeof value === "string") {
    try {
      return JSON.parse(value) as Record<string, any>;
    } catch (error) {
      return {};
    }
  }

  return value as Record<string, any>;
}

function formatDateInput(value?: string | null) {
  return value ? String(value).slice(0, 10) : "";
}

function normalizeEventenaLabel(value?: string | null, fallback = "Eventena") {
  const label = String(value || "").trim();
  const legacyLabel = ["ash", "vish events"].join("-");

  return !label || label.toLowerCase() === legacyLabel ? fallback : label;
}

function normalizeAdjustmentsTitle(value?: unknown) {
  const title = String(value || "").trim();
  return !title || title === "Additional Charges / Adjustments" ? "Event Manager Charges" : title;
}

function createDraftFromQuotation(quotation: any): QuotationPrintHeaderDraft {
  const headerConfig = parseJsonField(quotation.header_config);
  const footerConfig = parseJsonField(quotation.footer_config);
  const eventOccasion = headerConfig.event_occasion || quotation.event_occasion || "";
  const eventDate = headerConfig.event_start_at || quotation.event_start_at;

  return {
    quoteNumber: quotation.quote_number || "",
    issuedByLabel: normalizeEventenaLabel(quotation.issued_by_label),
    status: quotation.status || "",
    title: quotation.title || "",
    clientMobile: headerConfig.client_mobile || quotation.client_mobile || "",
    quotationType: headerConfig.quotation_type || quotation.quotation_type || "Wedding",
    headerTagline: quotation.header_tagline || "Crafted with care - Executed with excellence",
    eventFamily: headerConfig.event_host_family_name || quotation.event_host_family_name || quotation.customer_name || "",
    eventGroom: headerConfig.event_groom_name || quotation.event_groom_name || "",
    eventBride: headerConfig.event_bride_name || quotation.event_bride_name || "",
    eventDate: formatDateInput(eventDate),
    eventVenue: headerConfig.event_venue || quotation.event_venue || "",
    eventOccasion,
    eventDecorType: headerConfig.event_decor_type_label || quotation.event_decor_type_label || "",
    paymentQrTitle: footerConfig.payment_qr_title || "Scan to Pay",
    paymentQrNote: footerConfig.payment_qr_note || "Use this QR code for booking and milestone payments.",
    footerTagline: quotation.footer_tagline || "Creating unforgettable moments with professional excellence",
    footerPhone: footerConfig.footer_phone || quotation.footer_phone || "Mobile: +919405076050 +917972426493",
    totalNote: quotation.total_note || "Exclusive of diesel, food & extra permissions",
    showItemPrice: quotation.show_item_price !== false && quotation.show_item_price !== 0,
    showItemAmount: quotation.show_item_amount !== false && quotation.show_item_amount !== 0,
    showAdjustments: Boolean((quotation.adjustments || []).length),
    adjustmentsTitle: normalizeAdjustmentsTitle(quotation.adjustments_title),
    showAdjustmentPercentage: true,
    showAdjustmentAmount: true,
    showPaymentDetails: true,
    showScanToPay: footerConfig.show_scan_to_pay !== false && footerConfig.show_scan_to_pay !== 0,
    paymentMethod: quotation.payment_method || "Cash",
    partialTotalAmount: Number(quotation.partial_total_amount || 0),
    partialAmountPaid: Number(quotation.partial_amount_paid || 0),
    partialTotalManual: false,
    paymentTransactions: [],
    sections: (quotation.sections || []).map((section: any) => ({
      id: section.id,
      templateSectionId: section.template_section_id,
      serviceCategoryId: section.service_category_id,
      sectionCode: section.section_code || "",
      sectionTitle: section.section_title || "",
      sectionDescription: section.section_description || "",
      isVisible: section.is_visible !== false && section.is_visible !== 0,
      isOptional: Boolean(section.is_optional),
      showSectionTotal: Boolean(section.show_section_total),
      sectionNote: section.section_note || "",
      items: (section.items || []).map((item: any) => ({
        id: item.id,
        templateItemId: item.template_item_id,
        catalogItemId: item.catalog_item_id,
        itemCode: item.item_code || "",
        itemName: item.item_name || "",
        itemDescription: item.item_description || "",
        quantity: Number(item.quantity || 0),
        unit: item.unit || "",
        dimensionText: item.dimension_text || "",
        pricingMode: item.pricing_mode || "manual",
        unitRate: Number(item.unit_rate || 0),
        lineTotal: Number(item.line_total || 0),
        discountType: item.discount_type || "none",
        discountValue: Number(item.discount_value || 0),
        taxRate: Number(item.tax_rate || 0),
        isOptional: Boolean(item.is_optional),
        includeInTotal: item.include_in_total !== false && item.include_in_total !== 0,
        isVisible: item.is_visible !== false && item.is_visible !== 0,
        remarks: item.remarks || ""
      }))
    })),
    notes: (quotation.notes || []).map((note: any) => ({
      templateNoteId: note.template_note_id,
      title: note.title || "",
      icon: note.icon || "",
      isVisible: note.is_visible !== false && note.is_visible !== 0,
      points: (note.points || []).map((point: any) => ({
        templateNotePointId: point.template_note_point_id,
        pointText: point.point_text || "",
        isHighlighted: Boolean(point.is_highlighted)
      }))
    })),
    paymentMilestones: (quotation.paymentMilestones || []).map((milestone: any) => ({
      templatePaymentMilestoneId: milestone.template_payment_milestone_id,
      milestoneLabel: milestone.milestone_label || "",
      percentage: Number(milestone.percentage || 0),
      dueRuleText: milestone.due_rule_text || "",
      dueDate: formatDateInput(milestone.due_date),
      amount: Number(milestone.amount || 0),
      description: milestone.description || "",
      paymentStatus: milestone.payment_status || "pending",
      isVisible: milestone.is_visible !== false && milestone.is_visible !== 0
    })),
    terms: (quotation.terms || []).map((term: any) => ({
      templateTermId: term.template_term_id,
      termText: term.term_text || "",
      isMandatory: term.is_mandatory !== false && term.is_mandatory !== 0,
      isVisible: term.is_visible !== false && term.is_visible !== 0
    })),
    adjustments: (quotation.adjustments || []).map((adjustment: any) => ({
      label: adjustment.label || "",
      calculationType: adjustment.calculation_type || "fixed",
      calculationValue: Number(adjustment.calculation_value ?? adjustment.amount ?? 0),
      amount: Number(adjustment.amount || 0),
      isVisible: adjustment.is_visible !== false && adjustment.is_visible !== 0
    }))
  };
}

function mapQuotationForUpdate(quotation: any, draft: QuotationPrintHeaderDraft) {
  const currentFooterConfig = parseJsonField(quotation.footer_config);
  const headerConfig = {
    ...parseJsonField(quotation.header_config),
    event_host_family_name: draft.eventFamily,
    event_groom_name: draft.eventGroom,
    event_bride_name: draft.eventBride,
    event_start_at: draft.eventDate,
    event_venue: draft.eventVenue,
    event_occasion: draft.eventOccasion,
    event_decor_type_label: draft.eventDecorType,
    client_mobile: draft.clientMobile,
    quotation_type: draft.quotationType
  };
  const footerConfig = {
    ...currentFooterConfig,
    payment_qr_title: draft.paymentQrTitle,
    payment_qr_note: draft.paymentQrNote,
    show_scan_to_pay: draft.showScanToPay !== false,
    footer_phone: draft.footerPhone
  };

  return {
    quotation: {
      organizationProfileId: quotation.organization_profile_id,
      customerId: quotation.customer_id,
      eventId: quotation.event_id,
      templateId: quotation.template_id,
      quoteNumber: draft.quoteNumber,
      revisionNo: quotation.revision_no || 0,
      status: draft.status,
      pricingMode: quotation.pricing_mode || "scope_only",
      title: draft.title,
      subtitle: quotation.subtitle || "",
      headerBrandName: normalizeEventenaLabel(quotation.header_brand_name, "EVENTENA"),
      headerTagline: draft.headerTagline,
      footerBrandName: normalizeEventenaLabel(quotation.footer_brand_name, "EVENTENA"),
      footerTagline: draft.footerTagline,
      issuedByLabel: draft.issuedByLabel,
      issueDate: formatDateInput(quotation.issue_date),
      validUntil: formatDateInput(quotation.valid_until),
      currencyCode: quotation.currency_code || "INR",
      showItemPrice: draft.showItemPrice,
      showItemAmount: draft.showItemAmount,
      showSectionTotals: Boolean(quotation.show_section_totals),
      showNotes: quotation.show_notes !== false && quotation.show_notes !== 0,
      showPaymentSchedule: quotation.show_payment_schedule !== false && quotation.show_payment_schedule !== 0,
      showTerms: quotation.show_terms !== false && quotation.show_terms !== 0,
      themeTokens: parseJsonField(quotation.theme_tokens),
      headerConfig,
      footerConfig,
      printConfig: parseJsonField(quotation.print_config),
      customerMessage: quotation.customer_message || "",
      internalNotes: quotation.internal_notes || "",
      quoteDiscountTotal: Number(quotation.quote_discount_total || 0),
      roundOffAmount: Number(quotation.round_off_amount || 0),
      grandTotalWords: quotation.grand_total_words || "",
      totalNote: draft.totalNote || ""
    },
    sections: draft.sections.map((section) => ({
      templateSectionId: section.templateSectionId,
      serviceCategoryId: section.serviceCategoryId,
      sectionCode: section.sectionCode,
      sectionTitle: section.sectionTitle,
      sectionDescription: section.sectionDescription || "",
      isVisible: section.isVisible,
      isOptional: section.isOptional,
      showSectionTotal: section.showSectionTotal,
      sectionNote: section.sectionNote || "",
      items: section.items.map((item) => ({
        templateItemId: item.templateItemId,
        catalogItemId: item.catalogItemId,
        itemCode: item.itemCode,
        itemName: item.itemName,
        itemDescription: item.itemDescription || "",
        quantity: Number(item.quantity || 0),
        unit: item.unit || "",
        dimensionText: item.dimensionText || "",
        pricingMode: item.pricingMode || "manual",
        unitRate: Number(item.unitRate || 0),
        discountType: item.discountType || "none",
        discountValue: Number(item.discountValue || 0),
        taxRate: Number(item.taxRate || 0),
        isOptional: Boolean(item.isOptional),
        includeInTotal: item.includeInTotal,
        isVisible: item.isVisible,
        remarks: item.remarks || ""
      }))
    })),
    adjustments: (quotation.adjustments || []).map((adjustment: any) => ({
      adjustmentType: adjustment.adjustment_type,
      label: adjustment.label,
      calculationType: adjustment.calculation_type,
      calculationValue: Number(adjustment.calculation_value || 0),
      amount: Number(adjustment.amount || 0),
      isTaxable: Boolean(adjustment.is_taxable),
      isVisible: adjustment.is_visible !== false && adjustment.is_visible !== 0,
      remarks: adjustment.remarks || ""
    })),
    notes: draft.notes.map((note) => ({
      templateNoteId: note.templateNoteId,
      title: note.title,
      icon: note.icon || "",
      isVisible: note.isVisible,
      points: note.points.map((point) => ({
        templateNotePointId: point.templateNotePointId,
        pointText: point.pointText,
        isHighlighted: Boolean(point.isHighlighted)
      }))
    })),
    terms: draft.terms.map((term) => ({
      templateTermId: term.templateTermId,
      termText: term.termText,
      isMandatory: term.isMandatory,
      isVisible: term.isVisible
    })),
    paymentMilestones: draft.paymentMilestones.map((milestone) => ({
      templatePaymentMilestoneId: milestone.templatePaymentMilestoneId,
      milestoneLabel: milestone.milestoneLabel,
      percentage: Number(milestone.percentage || 0),
      dueRuleText: milestone.dueRuleText || "",
      dueDate: milestone.dueDate || "",
      amount: Number(milestone.amount || 0),
      description: milestone.description || "",
      paymentStatus: milestone.paymentStatus || "pending",
      isVisible: milestone.isVisible
    })),
    recipients: (quotation.recipients || []).map((recipient: any) => ({
      customerContactId: recipient.customer_contact_id,
      userId: recipient.user_id,
      recipientName: recipient.recipient_name,
      recipientEmail: recipient.recipient_email || "",
      recipientPhone: recipient.recipient_phone || "",
      roleLabel: recipient.role_label || "",
      isPrimary: Boolean(recipient.is_primary),
      canView: recipient.can_view !== false && recipient.can_view !== 0,
      canRespond: recipient.can_respond !== false && recipient.can_respond !== 0
    }))
  };
}

export default function QuotationPrintPage() {
  const { quotationId } = useParams();
  const navigate = useNavigate();
  const [quotation, setQuotation] = useState<any>(null);
  const [draft, setDraft] = useState<QuotationPrintHeaderDraft | null>(null);
  const [editingPreview, setEditingPreview] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [preparingPrint, setPreparingPrint] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const response = await adminApi.get<any>(`/quotations/${quotationId}`);
        setQuotation(response);
        setDraft(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load quotation");
      } finally {
        setLoading(false);
      }
    };

    void load();
  }, [quotationId]);

  const handleDraftChange = (key: keyof QuotationPrintHeaderDraft, value: any) => {
    setDraft((current) => (current ? { ...current, [key]: value } : current));
  };

  const cancelPreviewEdit = () => {
    setDraft(null);
    setEditingPreview(false);
    setError(null);
  };

  const savePreviewEdit = async () => {
    if (!quotationId || !quotation || !draft) {
      return;
    }

    try {
      setSaving(true);
      setError(null);
      await adminApi.put(`/quotations/${quotationId}`, mapQuotationForUpdate(quotation, draft));
      const response = await adminApi.get<any>(`/quotations/${quotationId}`);
      setQuotation(response);
      setDraft(null);
      setEditingPreview(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save quotation changes");
    } finally {
      setSaving(false);
    }
  };

  const handlePrintQuotation = async () => {
    try {
      setPreparingPrint(true);
      setError(null);
      const fonts = (document as any).fonts;
      await fonts?.ready;
      const imagePromises = Array.from(document.images).map((image) => {
        if (image.complete && image.naturalWidth > 0) {
          return Promise.resolve();
        }

        if (typeof image.decode === "function") {
          return image.decode().catch(() => undefined);
        }

        return new Promise<void>((resolve) => {
          image.addEventListener("load", () => resolve(), { once: true });
          image.addEventListener("error", () => resolve(), { once: true });
        });
      });
      await Promise.all(imagePromises);
      window.print();
    } finally {
      setPreparingPrint(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: "60vh", display: "grid", placeItems: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Stack spacing={2} sx={{ pb: editingPreview ? 10 : 0 }}>
      <Box sx={{ height: { xs: editingPreview ? 128 : 82, sm: 82 }, displayPrint: "none" }} />
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.5}
        useFlexGap
        flexWrap="wrap"
        sx={{
          position: "fixed",
          top: { xs: 76, md: 80 },
          left: { xs: "50%", md: "calc(272px + ((100vw - 272px) / 2))" },
          transform: "translateX(-50%)",
          zIndex: 1300,
          width: "fit-content",
          maxWidth: { xs: "calc(100vw - 28px)", md: "calc(100vw - 304px)" },
          displayPrint: "none",
          p: 1.25,
          borderRadius: 3,
          background: "rgba(255, 250, 244, 0.98)",
          border: "1px solid rgba(118, 93, 68, 0.14)",
          boxShadow: "0 10px 30px rgba(15, 23, 42, 0.14)",
          backdropFilter: "blur(10px)"
        }}
      >
        <Button startIcon={<ArrowBackRoundedIcon />} variant="outlined" onClick={() => navigate("/admin/quotations")}>
          Back to quotations
        </Button>
        {editingPreview ? (
          <>
            <Button startIcon={<SaveRoundedIcon />} variant="contained" onClick={() => void savePreviewEdit()} disabled={saving}>
              {saving ? "Saving..." : "Save changes"}
            </Button>
            <Button variant="outlined" onClick={cancelPreviewEdit} disabled={saving}>
              Cancel
            </Button>
          </>
        ) : (
          <Button
            startIcon={<EditRoundedIcon />}
            variant="outlined"
            onClick={() => {
              if (quotation && !draft) {
                setDraft(createDraftFromQuotation(quotation));
              }
              setEditingPreview(true);
            }}
          >
            Edit on preview
          </Button>
        )}
        <Button startIcon={<PrintRoundedIcon />} variant="contained" onClick={() => void handlePrintQuotation()} disabled={editingPreview || preparingPrint}>
          {preparingPrint ? "Preparing PDF..." : "Print / Save PDF"}
        </Button>
      </Stack>

      {error ? <Alert severity="error">{error}</Alert> : null}

      <QuotationPrintView quotation={quotation} editing={editingPreview} draft={draft || undefined} onDraftChange={handleDraftChange} />

      {editingPreview ? (
        <Stack
          direction="row"
          spacing={1.25}
          useFlexGap
          flexWrap="wrap"
          justifyContent="center"
          sx={{
            position: "fixed",
            left: { xs: 12, md: "calc(272px + 16px)" },
            right: 16,
            bottom: 16,
            zIndex: 1300,
            displayPrint: "none",
            p: 1.25,
            borderRadius: 3,
            background: "rgba(255, 250, 244, 0.98)",
            border: "1px solid rgba(118, 93, 68, 0.14)",
            boxShadow: "0 14px 36px rgba(15, 23, 42, 0.18)",
            backdropFilter: "blur(10px)"
          }}
        >
          <Button startIcon={<SaveRoundedIcon />} variant="contained" onClick={() => void savePreviewEdit()} disabled={saving}>
            {saving ? "Saving..." : "Save changes"}
          </Button>
          <Button variant="outlined" onClick={cancelPreviewEdit} disabled={saving}>
            Cancel
          </Button>
        </Stack>
      ) : null}
    </Stack>
  );
}
